// src/components/PatientSelector.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Icon from './Icon.js';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function PatientSelector({ token, onPatientSelect }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadPatients();
  }, []);

  const loadPatients = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('🔄 Loading patients for therapist...');
      
      const response = await axios.get(`${API_BASE}/api/invitations/my-patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ Patients loaded:', response.data);
      setPatients(response.data.patients);
      
    } catch (err) {
      console.error('❌ Error loading patients:', err);
      setError('Kunde inte ladda klienter');
    } finally {
      setLoading(false);
    }
  };

  const handlePatientClick = (patient) => {
    console.log('🔄 Selecting patient:', patient);
    onPatientSelect(patient);
  };

  const getPatientInitials = (patient) => {
    const firstName = patient.first_name || patient.name.split(' ')[0] || '';
    const lastName = patient.last_name || patient.name.split(' ')[1] || '';
    return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64 bg-white rounded-lg shadow-sm">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Laddar dina patienter...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          Välj Patient
        </h2>
        <p className="text-gray-600">Klicka på en patient för att se deras AI-chat och sessioner</p>
      </div> */}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800 mb-3">⚠️ {error}</p>
          <button 
            onClick={loadPatients}
            className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors font-medium"
          >
            Försök igen
          </button>
        </div>
      )}

      {patients.length === 0 && !error ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Icon name="users" size={64} className="text-gray-400 mx-auto mb-6" />
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Inga klienter anslutna</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Du har inga klienter anslutna ännu. Skapa en inbjudning för att bjuda in din första klient.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-lg mx-auto">
            <p className="text-blue-800 text-sm flex items-center gap-2">
              <Icon name="lightbulb" size={16} />
              <strong>Tips:</strong> Gå till <Icon name="mail" size={14} className="inline" /> Inbjudningar för att skapa en inbjudningskod som du kan dela med din klient.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {patients.map(patient => (
            <div 
              key={patient.id} 
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer group"
              onClick={() => handlePatientClick(patient)}
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-semibold text-lg">
                  {getPatientInitials(patient)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 truncate">{patient.name}</h3>
                  <p className="text-sm text-gray-500 truncate">{patient.email}</p>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                {patient.phone && (
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <Icon name="phone" size={14} />
                    {patient.phone}
                  </p>
                )}
                {patient.date_of_birth && (
                  <p className="text-sm text-gray-600 flex items-center gap-2">
                    <Icon name="calendar" size={14} />
                    {formatDate(patient.date_of_birth)}
                  </p>
                )}
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Icon name="calendar" size={14} />
                  Ansluten: {formatDate(patient.assigned_date)}
                </p>
              </div>
              
              <div className="pt-4 border-t border-gray-100">
                <button className="w-full py-2 px-4 bg-blue-50 text-blue-700 rounded-md group-hover:bg-blue-100 transition-colors font-medium text-sm flex items-center justify-center gap-2">
                  Välj Klient
                  <Icon name="arrow-right" size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-4 text-center">
        <p className="text-gray-600 text-sm flex items-center justify-center gap-2">
          <Icon name="users" size={16} />
          Totalt {patients.length} klient{patients.length !== 1 ? 'er' : ''} ansluten{patients.length !== 1 ? 'a' : ''}
        </p>
      </div>
    </div>
  );
}

export default PatientSelector;